import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { MessageCircle, Send, Sparkles, X, CheckCircle2, AlertCircle, Zap } from 'lucide-react'
import { useOrionAI } from '../hooks/useOrionAI'
import { useOrionAIContext } from '../contexts/OrionAIContext'
import { useToast } from './Toast'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useMaterialLibrary } from '../hooks/useMaterialLibrary'

/** Rendert berichttekst met klikbare links [tekst](url) */
function MessageWithLinks({ content, onLinkClick }) {
  if (!content || typeof content !== 'string') return null
  const parts = []
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g
  let lastIndex = 0
  let match
  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', value: content.slice(lastIndex, match.index) })
    }
    parts.push({ type: 'link', text: match[1], url: match[2] })
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < content.length) {
    parts.push({ type: 'text', value: content.slice(lastIndex) })
  }
  if (parts.length === 0) return <span className="whitespace-pre-wrap">{content}</span>
  return (
    <span className="whitespace-pre-wrap">
      {parts.map((p, i) =>
        p.type === 'text' ? (
          <span key={i}>{p.value}</span>
        ) : (
          <button
            key={i}
            type="button"
            onClick={() => onLinkClick?.(p.url)}
            className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
          >
            {p.text}
          </button>
        )
      )}
    </span>
  )
}

/**
 * Dropdown voor WhatsApp-bonnen die nog niet aan een project gekoppeld zijn.
 * Koppelt bon aan project via PUT /receipts/{id}/assign.
 */
function ReceiptAssignDropdown({ receipt, projects, onAssignSuccess, onNavigate, t }) {
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleAssign = async () => {
    const projectId = Number(selectedProjectId)
    if (!projectId || !receipt?.id) return
    setIsSubmitting(true)
    try {
      await api.put(`/receipts/${receipt.id}/assign`, { project_id: projectId })
      onAssignSuccess()
      onNavigate()
    } catch (err) {
      console.error('Receipt assign error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">
        {t('receipts.assignTo', 'Toewijzen aan project')}:
      </p>
      <div className="flex items-center gap-2">
        <select
          value={selectedProjectId}
          onChange={(e) => setSelectedProjectId(e.target.value)}
          disabled={isSubmitting}
          className="flex-1 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 bg-white dark:bg-gray-700"
        >
          <option value="">{t('receipts.selectProject', 'Selecteer project...')}</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name || p.naam || `Project ${p.id}`} {p.klant_naam ? `(${p.klant_naam})` : ''}
            </option>
          ))}
        </select>
        <button
          onClick={handleAssign}
          disabled={!selectedProjectId || isSubmitting}
          className="px-2 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? '...' : t('receipts.assignButton', 'Koppel')}
        </button>
      </div>
      <button
        onClick={onNavigate}
        className="mt-2 text-xs text-amber-700 dark:text-amber-300 hover:underline"
      >
        {t('receipts.openInbox', 'Open Bonnen Inbox')} →
      </button>
    </div>
  )
}

/**
 * Dropdown voor niet-gemapte materialen (material_id null in material_calculator_ingest).
 * Koppelt items aan UniverseelMateriaal via PATCH import-metadata.
 */
function UnmappedMaterialsDropdown({
  unmappedItems,
  projectId,
  materials,
  isLoading,
  materialSelections,
  onSelectionChange,
  onMappingSuccess,
  onNavigate,
  t,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleKoppel = async () => {
    const mappings = Object.entries(materialSelections)
      .filter(([, id]) => id != null && id !== '')
      .map(([idx, materialId]) => ({ ingest_index: Number(idx), material_id: Number(materialId) }))
    if (mappings.length === 0) {
      onNavigate()
      return
    }
    setIsSubmitting(true)
    try {
      await api.patch(`/projecten/${projectId}/import-metadata/material-mapping`, {
        material_mappings: mappings,
      })
      onMappingSuccess()
      // Vernieuw window.__orionRecentImport lokaal (backend is bijgewerkt)
      if (typeof window !== 'undefined' && window.__orionRecentImport?.project_id === projectId) {
        const ingest = [...(window.__orionRecentImport.material_calculator_ingest || [])]
        mappings.forEach(({ ingest_index, material_id }) => {
          if (ingest[ingest_index]) ingest[ingest_index] = { ...ingest[ingest_index], material_id }
        })
        window.__orionRecentImport = { ...window.__orionRecentImport, material_calculator_ingest: ingest }
      }
    } catch (err) {
      console.error('Material mapping error:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="mt-3 p-2 bg-amber-50 dark:bg-amber-900/20 rounded border border-amber-200 dark:border-amber-800">
      <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 mb-2">
        {t('aiAssistant.mapMaterialDropdown', 'Koppel aan UniverseelMateriaal:')}
      </p>
      <div className="space-y-1.5 max-h-32 overflow-y-auto">
        {unmappedItems.map((item) => {
          const idx = item._ingest_index ?? unmappedItems.indexOf(item)
          const dims = item.dimensions_mm || {}
          const label = [item.description?.slice(0, 40), dims.width && dims.height ? `${dims.width}×${dims.height}mm` : null]
            .filter(Boolean)
            .join(' · ')
          return (
            <div key={idx} className="flex items-center gap-2">
              <span className="text-xs text-gray-600 dark:text-gray-400 truncate flex-1 min-w-0" title={item.description}>
                {label || `Item ${idx + 1}`}
              </span>
              <select
                value={materialSelections[idx] ?? ''}
                onChange={(e) => onSelectionChange(idx, e.target.value ? Number(e.target.value) : null)}
                disabled={isLoading}
                className="flex-shrink-0 text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 max-w-[140px]"
              >
                <option value="">{t('aiAssistant.selectMaterial', '— Selecteer —')}</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.id}>
                    {(m.display_naam || m.naam || '').slice(0, 25)}
                  </option>
                ))}
              </select>
            </div>
          )
        })}
      </div>
      <div className="mt-2 flex gap-2">
        <button
          onClick={handleKoppel}
          disabled={isSubmitting || Object.keys(materialSelections).length === 0}
          className="px-2 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? '...' : t('aiAssistant.koppelMaterialen', 'Koppel in project')}
        </button>
        <button
          onClick={onNavigate}
          className="px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-300 hover:underline"
        >
          {t('aiAssistant.gaNaarProject', 'Ga naar project')}
        </button>
      </div>
    </div>
  )
}

/**
 * Orion AI Assistant Component
 * Chat interface voor natuurlijke taal commando's.
 * Context-aware: heeft toegang tot huidige project/offerte/kast state.
 */
function OrionAIAssistant({ isOpen, onClose, onActionCompleted }) {
  const { t } = useTranslation()
  const { aiContext } = useOrionAIContext()
  const { 
    messages, 
    sendMessage, 
    executeAICommand, 
    executeAction,
    clearMessages,
    addProactiveMessage,
    isProcessing, 
    aiAvailable,
  } = useOrionAI(aiContext)
  const [input, setInput] = useState('')
  const [suggestions, setSuggestions] = useState([])
  const [executingAction, setExecutingAction] = useState(null)
  const [proactiveAlert, setProactiveAlert] = useState(null) // optional: { message, action }
  const [materialSelections, setMaterialSelections] = useState({}) // { ingest_index: material_id }
  const messagesEndRef = useRef(null)
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { materials: libraryMaterials, isLoading: materialsLoading } = useMaterialLibrary({ type: 'all', limit: 500, enabled: isOpen })
  const shownReceiptIdsRef = useRef(new Set())

  const { data: projects = [] } = useQuery({
    queryKey: ['projecten', 'active'],
    queryFn: async () => {
      const res = await api.get('/projecten', { params: { is_archived: false, limit: 200 } })
      return res.data || []
    },
    enabled: isOpen && !!aiContext.whatsapp_receipts?.receipts?.length,
  })

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  // Phase 2: Luister naar PDF import complete – proactief Guardian-bericht met Generative UI
  // Strikte mapping op import_metadata.material_calculator_ingest; unmapped_items = material_id null/ontbreekt
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail || {}
      const projectId = detail.project_id
      const mappedCount = detail.mapped_count ?? 0
      const unmappedCount = detail.unmapped_count ?? 0
      const unmappedItems = detail.unmapped_items || []
      const unmappedDescriptions = detail.unmapped_descriptions || []
      const totalCabinets = mappedCount + unmappedCount

      const msg = totalCabinets > 0
        ? t('aiAssistant.pdfImportSuccess', 'Ik heb het project succesvol geïmporteerd. {{count}} kasten zijn klaar en materialen zijn gekoppeld.', { count: mappedCount })
        : t('aiAssistant.pdfImportSuccessNoCabinets', 'Ik heb het project succesvol geïmporteerd.')

      const commands = []
      if (projectId) {
        commands.push({
          type: 'NAVIGATE',
          label: t('aiAssistant.reviewMappedMaterials', 'Bekijk gekoppelde materialen'),
          payload: { url: `/projecten/${projectId}`, label: t('aiAssistant.reviewMappedMaterials', 'Bekijk gekoppelde materialen') },
        })
        commands.push({
          label: t('aiAssistant.generateCuttingSchedule', 'Genereer zaagschema'),
          command: t('aiAssistant.generateCuttingScheduleCommand', 'Genereer zaagschema voor dit project'),
        })
      }

      addProactiveMessage(msg, { commands })

      if (unmappedCount > 0) {
        const warnMsg = t('aiAssistant.pdfImportUnmappedWarning', '{{count}} materiaal(len) konden niet automatisch gekoppeld worden: {{list}}', {
          count: unmappedCount,
          list: unmappedDescriptions.slice(0, 5).join(', ') + (unmappedDescriptions.length > 5 ? '...' : ''),
        })
        addProactiveMessage(warnMsg, {
          commands: [{
            type: 'NAVIGATE',
            label: t('aiAssistant.selectAlternativeFromStock', 'Selecteer alternatief uit voorraad'),
            payload: { url: `/projecten/${projectId}`, label: t('aiAssistant.selectAlternativeFromStock', 'Selecteer alternatief uit voorraad') },
          }],
          unmapped_items: unmappedItems,
          project_id: projectId,
        })
      }
    }
    window.addEventListener('orion-pdf-import-complete', handler)
    return () => window.removeEventListener('orion-pdf-import-complete', handler)
  }, [addProactiveMessage, t])

  // WhatsApp Receipt Scanner: proactieve notificatie voor on toegewezen bonnen
  useEffect(() => {
    const receipts = aiContext.whatsapp_receipts?.receipts
    if (!isOpen || !Array.isArray(receipts) || receipts.length === 0) return

    const newReceipts = receipts.filter((r) => r.id && !shownReceiptIdsRef.current.has(r.id))
    if (newReceipts.length === 0) return

    newReceipts.forEach((r) => {
      shownReceiptIdsRef.current.add(r.id)
      const supplier = r.supplier_name || 'Onbekend'
      const amount = r.total_amount != null ? `€ ${(r.total_amount / 100).toFixed(2)}` : '—'
      const msg = t('receipts.guardianNewReceipt', 'Ik heb een nieuwe bon ontvangen van {{supplier}} voor {{amount}}. Aan welk project wil je deze koppelen?', {
        supplier,
        amount,
      })
      addProactiveMessage(msg, {
        whatsapp_receipts: [r],
        commands: [
          {
            type: 'NAVIGATE',
            label: t('receipts.openInbox', 'Open Bonnen Inbox'),
            payload: { url: '/receipts/inbox', label: t('receipts.openInbox', 'Open Bonnen Inbox') },
          },
        ],
      })
    })
  }, [isOpen, aiContext.whatsapp_receipts, addProactiveMessage, t])

  const handleSend = async () => {
    if (!input.trim() || isProcessing) return

    const userMessage = input.trim()
    setInput('')

    try {
      const result = await sendMessage(userMessage)

      // Update suggestions from result
      if (result?.next_suggestions && result.next_suggestions.length > 0) {
        setSuggestions(result.next_suggestions.slice(0, 4))
      } else {
        // Also check last message for suggestions
        const lastMessage = messages[messages.length - 1]
        if (lastMessage?.next_suggestions && lastMessage.next_suggestions.length > 0) {
          setSuggestions(lastMessage.next_suggestions.slice(0, 4))
        }
      }

      // Handle successful actions
      if (result?.success) {
        const createdObjects = result.created_objects || {}
        
        // Show success feedback
        if (Object.keys(createdObjects).length > 0) {
          toast.success(t('aiAssistant.actieSuccesvol', 'Actie succesvol voltooid!'))
          
          // Emit action completed event
          if (onActionCompleted) {
            onActionCompleted(result)
          }
        }

        // Handle navigation actions
        if (result.actions && Array.isArray(result.actions)) {
          for (const action of result.actions) {
            if (action.type === 'NAVIGATE' && action.payload?.url) {
              // Use React Router navigate
              navigate(action.payload.url)
            }
          }
        }
      }
    } catch (error) {
      // Error is already handled by sendMessage hook
      console.error('AI Assistant error:', error)
    }
  }
  
  // Update suggestions when messages change
  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    if (lastMessage?.next_suggestions && lastMessage.next_suggestions.length > 0) {
      setSuggestions(lastMessage.next_suggestions.slice(0, 4))
    }
  }, [messages])

  const handleSuggestionClick = (suggestion) => {
    setInput(suggestion)
  }

  const handleActionClick = async (cmdOrAction) => {
    if (!cmdOrAction) return
    setExecutingAction(t('aiAssistant.actieUitvoeren', 'Actie uitvoeren...'))
    try {
      if (cmdOrAction.type === 'NAVIGATE' && cmdOrAction.payload?.url) {
        navigate(cmdOrAction.payload.url)
        toast.success(t('aiAssistant.actieSuccesvol', 'Actie succesvol voltooid!'))
        if (onActionCompleted) onActionCompleted({ success: true })
      } else if (cmdOrAction.type && cmdOrAction.payload) {
        await executeAction(cmdOrAction, cmdOrAction.payload)
      } else if (cmdOrAction.type) {
        await executeAction({ type: cmdOrAction.type, payload: cmdOrAction.payload || {} })
      } else {
        await executeAICommand(cmdOrAction.label || cmdOrAction.command)
      }
      if (cmdOrAction.type !== 'NAVIGATE') {
        toast.success(t('aiAssistant.actieSuccesvol', 'Actie succesvol voltooid!'))
        if (onActionCompleted) onActionCompleted({ success: true })
      }
    } catch (err) {
      toast.error(err.message || t('aiAssistant.actieFout', 'Actie mislukt'))
    } finally {
      setExecutingAction(null)
    }
  }

  if (!isOpen) return null

  // Show context indicator
  const contextInfo = aiContext.project 
    ? t('aiAssistant.contextProject', 'Project: {{naam}}', { naam: aiContext.project.naam || aiContext.project.name })
    : aiContext.quote 
    ? t('aiAssistant.contextQuote', 'Offerte #{{id}}', { id: aiContext.quote.id })
    : aiContext.cabinet
    ? t('aiAssistant.contextKast', 'Kast Configurator')
    : null

  return (
    <div className="fixed bottom-4 right-4 w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 flex flex-col dark:bg-gray-800 dark:border-gray-700" style={{ height: '600px' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5" />
          <div>
            <h3 className="font-bold text-lg">{t('aiAssistant.title', 'Orion AI Assistant')}</h3>
            {contextInfo && (
              <p className="text-xs text-blue-100 mt-0.5">{contextInfo}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!aiAvailable && (
            <div className="text-xs bg-yellow-500 px-2 py-1 rounded" title={t('aiAssistant.aiNietBeschikbaar', 'AI tijdelijk niet beschikbaar')}>
              ⚠️
            </div>
          )}
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Proactive Alert */}
        {proactiveAlert && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-yellow-800 mb-2">{proactiveAlert.message}</p>
                {proactiveAlert.action && (
                  <button
                    onClick={proactiveAlert.action}
                    className="text-xs text-yellow-700 hover:text-yellow-900 underline font-semibold"
                  >
                    {t('aiAssistant.bekijkDetails', 'Bekijk details')} →
                  </button>
                )}
              </div>
              <button
                onClick={() => setProactiveAlert(null)}
                className="text-yellow-600 hover:text-yellow-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-blue-600" />
              </div>
            )}
            
            <div
              className={`max-w-[80%] rounded-lg p-3 ${
                msg.isError
                  ? 'bg-red-50 border border-red-200 text-red-800'
                  : msg.role === 'user'
                  ? 'bg-blue-50 border border-blue-200 text-blue-900'
                  : 'bg-green-50 border border-green-200 text-green-900'
              }`}
            >
              <p className="text-sm">
                <MessageWithLinks content={msg.content} onLinkClick={(url) => navigate(url)} />
              </p>
              {/* Mededeling over ontbrekende gegevens. Komt letterlijk uit de backend
                  en is bewust NIET door het model geschreven: een model dat mag
                  formuleren over zijn eigen ontbrekende data, vult het gat op met
                  wat plausibel klinkt. Daarom visueel gescheiden van het antwoord. */}
              {msg.mededeling && (
                <p className="mt-2 pt-2 border-t border-amber-300 text-xs text-amber-900 bg-amber-50 -mx-3 -mb-3 px-3 py-2 rounded-b-lg">
                  <span className="font-semibold">Onvolledig antwoord — </span>
                  {msg.mededeling}
                </p>
              )}
              {/* Generative UI: dropdown voor unmapped items (material_id null) – koppel aan UniverseelMateriaal */}
              {msg.role === 'assistant' && msg.unmapped_items?.length > 0 && msg.project_id && (
                <UnmappedMaterialsDropdown
                  unmappedItems={msg.unmapped_items}
                  projectId={msg.project_id}
                  materials={libraryMaterials}
                  isLoading={materialsLoading}
                  materialSelections={materialSelections}
                  onSelectionChange={(idx, materialId) =>
                    setMaterialSelections((prev) => ({ ...prev, [idx]: materialId }))
                  }
                  onMappingSuccess={() => {
                    setMaterialSelections({})
                    toast.success(t('aiAssistant.materialMappingSuccess', 'Materialen gekoppeld!'))
                    if (onActionCompleted) onActionCompleted({ success: true })
                  }}
                  onNavigate={() => navigate(`/projecten/${msg.project_id}`)}
                  t={t}
                />
              )}
              {/* Generative UI: dropdown voor WhatsApp-bonnen – koppel aan project */}
              {msg.role === 'assistant' && msg.whatsapp_receipts?.length > 0 && (
                msg.whatsapp_receipts.map((receipt) => (
                  <ReceiptAssignDropdown
                    key={receipt.id}
                    receipt={receipt}
                    projects={projects}
                    onAssignSuccess={() => {
                      queryClient.invalidateQueries(['receipts', 'inbox'])
                      queryClient.invalidateQueries(['receipts'])
                      shownReceiptIdsRef.current.delete(receipt.id)
                      toast.success(t('receipts.assigned', 'Bon gekoppeld aan project'))
                      if (onActionCompleted) onActionCompleted({ success: true })
                    }}
                    onNavigate={() => navigate('/receipts/inbox')}
                    t={t}
                  />
                ))
              )}
              {/* Generative UI: actionable buttons uit commands of actions */}
              {msg.role === 'assistant' && (msg.generativeCommands?.length > 0 || msg.actions?.length > 0) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {msg.generativeCommands?.map((cmd, i) => (
                    <button
                      key={`gen-${i}`}
                      onClick={() => handleActionClick(cmd)}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {cmd.label || cmd.type}
                    </button>
                  ))}
                  {msg.actions?.filter(a => a.type === 'NAVIGATE' || a.type === 'CHANGE_MATERIAL' || a.type === 'UPDATE_CABINET_DIMENSION').map((action, i) => (
                    <button
                      key={`act-${i}`}
                      onClick={() => handleActionClick(action)}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {action.payload?.label || (action.type === 'NAVIGATE' ? t('aiAssistant.navigeer', 'Navigeer') : action.type)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {msg.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-gray-600" />
              </div>
            )}
          </div>
        ))}
        
        {isProcessing && (
          <div className="flex gap-2 justify-start">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-blue-600 animate-pulse" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-600 animate-pulse" />
                <p className="text-sm text-gray-600">
                  {executingAction || t('aiAssistant.orionVerwerkt', '🛠️ Orion verwerkt je vraag...')}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {executingAction && !isProcessing && (
          <div className="flex gap-2 justify-start">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
              <Zap className="w-4 h-4 text-green-600" />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">{executingAction}</p>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
          <p className="text-xs font-semibold text-gray-600 mb-2">💡 {t('aiAssistant.suggesties', 'Suggesties')}:</p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => handleSuggestionClick(suggestion)}
                className="px-3 py-1 text-xs bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
            placeholder={aiContext.project 
              ? t('aiAssistant.placeholderProject', 'Vraag over {{naam}}...', { naam: aiContext.project.naam })
              : t('aiAssistant.placeholderDefault', "Typ je commando hier... (bijv. 'Maak klant Peeters aan')")
            }
            disabled={isProcessing || !aiAvailable}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
          <button
            onClick={handleSend}
            disabled={isProcessing || !input.trim() || !aiAvailable}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isProcessing ? (
              <Sparkles className="w-4 h-4 animate-pulse" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default OrionAIAssistant
